'use client'

import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import { useRouter } from 'next/navigation'
// Loading02Icon now imported from the solid set below so the spinner reads
// as a filled dot pattern instead of the thin stroke. (Stephen, 2026-05-06.)
import {
  Loading02Icon,
  AddMoneyCircleIcon,
  AiBrain01Icon,
  AiSearchIcon,
  AlertCircleIcon,
  ArrowDown01Icon,
  BrainIcon,
  Calendar04Icon,
  ChartAnalysisIcon,
  ChartBarLineIcon,
  ChartIcon,
  CopyIcon,
  DashboardCircleIcon,
  Database01Icon,
  Database02Icon,
  Delete01Icon,
  Dollar01Icon,
  DollarCircleIcon,
  FilterIcon,
  GlobeIcon,
  HealthIcon,
  Image01Icon,
  InformationCircleIcon,
  Link01Icon,
  Mail01Icon,
  MapsIcon,
  Megaphone01Icon,
  Megaphone02Icon,
  Notification01Icon,
  PaintBoardIcon,
  PencilEdit01Icon,
  PlayIcon,
  PauseIcon,
  RefreshIcon,
  RocketIcon,
  Search01Icon,
  Settings01Icon,
  SparklesIcon,
  ToolsIcon,
  TransactionIcon,
  Upload05Icon,
  UserGroupIcon,
  Video01Icon,
  Wallet01Icon,
} from '@hugeicons-pro/core-solid-rounded'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { InstallPromptModal } from '@/components/install-prompt/InstallPromptModal'
import { AudienceIngestProgress } from '@/components/chat/AudienceIngestProgress'
import { SupplyConfigWritePill } from '@/components/chat/SupplyConfigWritePill'

// ─── Tool registry ────────────────────────────────────────────────────────────

type ToolMode = 'action' | 'query'

interface ToolConfig {
  label: string
  mode: ToolMode
}

const TOOL_REGISTRY: Record<string, ToolConfig> = {
  __thinking: { label: 'Thinking', mode: 'query' },
  remember_user_preference: { label: 'Saved preference', mode: 'action' },
  create_campaign: { label: 'Created campaign', mode: 'action' },
  generate_video: { label: 'Generated video', mode: 'action' },
  pause_campaign: { label: 'Paused campaign', mode: 'action' },
  resume_campaign: { label: 'Resumed campaign', mode: 'action' },
  update_budget: { label: 'Updated budget', mode: 'action' },
  update_campaign: { label: 'Updated campaign', mode: 'action' },
  update_campaign_alerts: { label: 'Updated campaign alerts', mode: 'action' },
  update_brand: { label: 'Updated brand kit', mode: 'action' },
  generate_install_prompt: { label: 'Generated install prompt', mode: 'action' },
  create_video: { label: 'Started video generation', mode: 'action' },
  attach_creative: { label: 'Attached creative', mode: 'action' },
  launch_campaign: { label: 'Launched campaign', mode: 'action' },
  allocate_campaign_budget: { label: 'Allocated budget', mode: 'action' },
  add_wallet_funds: { label: 'Impressions purchase', mode: 'action' },
  // Admin-only tools (visible only in god mode — keep platform name).
  sonobi_query: { label: 'Network query', mode: 'query' },

  // Provider/SSP tools — black-boxed for customer-facing chat. The
  // underlying ad network is Atrium's implementation detail; receipts must
  // never name a specific platform (Stephen, 2026-05-06). All ua_* /
  // roku_* / sonobi_* / provision_* labels here use generic phrasing.
  provision_campaign_on_provider: { label: 'Provisioned for delivery', mode: 'action' },
  ua_create_campaign:    { label: 'Provisioned campaign', mode: 'action' },
  ua_create_adset:       { label: 'Configured ad set', mode: 'action' },
  ua_update_adset_targeting: { label: 'Updated targeting', mode: 'action' },
  ua_upload_creative:    { label: 'Uploaded creative', mode: 'action' },
  ua_create_ad:          { label: 'Linked creative to delivery', mode: 'action' },
  ua_archive_campaign:   { label: 'Archived campaign', mode: 'action' },
  ua_create_audience:    { label: 'Created audience', mode: 'action' },
  ua_create_audience_from_attachment: { label: 'Created audience', mode: 'action' },
  ua_get_audience_ingest_status: { label: 'Checked audience upload', mode: 'query' },
  ua_add_audience_users: { label: 'Added users to audience', mode: 'action' },
  ua_remove_audience_users: { label: 'Removed users from audience', mode: 'action' },
  ua_list_audiences:     { label: 'Loaded audiences', mode: 'query' },
  ua_get_campaign_report: { label: 'Fetched delivery report', mode: 'query' },
  ua_get_pixels:         { label: 'Loaded measurement pixels', mode: 'query' },
  ua_get_pixel_events:   { label: 'Loaded pixel events', mode: 'query' },
  get_roku_campaign_status: { label: 'Checked delivery status', mode: 'query' },
  get_roku_audiences:    { label: 'Loaded audiences', mode: 'query' },
  create_roku_audience:  { label: 'Created audience', mode: 'action' },
  sync_supply_performance: { label: 'Synced performance', mode: 'action' },
  setup_measurement:     { label: 'Set up measurement', mode: 'action' },
  set_pixel_credentials: { label: 'Saved measurement credentials', mode: 'action' },
  list_available_providers: { label: 'Checked delivery options', mode: 'query' },
  get_brand_context: { label: 'Looked up brand', mode: 'query' },
  get_campaigns: { label: 'Fetched campaigns', mode: 'query' },
  get_campaign_details: { label: 'Fetched campaign details', mode: 'query' },
  get_benchmarks: { label: 'Fetched benchmarks', mode: 'query' },
  get_creatives: { label: 'Loaded creatives', mode: 'query' },
  recall_workspace_context: { label: 'Recalled workspace context', mode: 'query' },
  get_platform_health: { label: 'Checked platform health', mode: 'query' },
  analyze_workspace: { label: 'Analyzed workspace', mode: 'query' },
  read_all_memory: { label: 'Read memory', mode: 'query' },
  query_platform_metrics: { label: 'Queried metrics', mode: 'query' },
  search_conversations: { label: 'Searched conversations', mode: 'query' },
  search_chat_history: { label: 'Searched chat history', mode: 'query' },
  get_wallet_balance: { label: 'Checked balance', mode: 'query' },
  check_wallet_balance: { label: 'Checked balance', mode: 'query' },
  setup_profile: { label: 'Setting up profile', mode: 'action' },
  setup_workspace: { label: 'Setting up workspace', mode: 'action' },
  check_matching_workspaces: { label: 'Checking workspaces', mode: 'query' },
  complete_onboarding: { label: 'Completing setup', mode: 'action' },
  get_ssp_campaign_status: { label: 'SSP campaign status', mode: 'query' },
  get_ssp_performance: { label: 'SSP performance', mode: 'query' },
  get_video_status: { label: 'Checked video status', mode: 'query' },
  list_elevenlabs_voices: { label: 'Listed available voices', mode: 'query' },
  get_wallet_transactions: { label: 'Fetched transactions', mode: 'query' },
  update_bid: { label: 'Updated bid', mode: 'action' },
  update_pacing: { label: 'Updated pacing', mode: 'action' },
  get_campaign_performance: { label: 'Campaign performance', mode: 'query' },
  get_atrium_performance_report: { label: 'Performance report', mode: 'query' },
  export_report_csv: { label: 'Export CSV', mode: 'query' },
  upload_creative_to_ssp: { label: 'Uploaded creative to SSP', mode: 'action' },
  scrape_brand: { label: 'Scraped brand', mode: 'action' },
  navigate: { label: 'Navigate', mode: 'action' },
  show_options: { label: 'Options', mode: 'action' },
  reinforce_memory: { label: 'Reinforced memory', mode: 'action' },
  update_notification_preferences: { label: 'Updated notifications', mode: 'action' },
  get_connected_channels: { label: 'Connected channels', mode: 'query' },
  delete_campaign: { label: 'Deleted campaign', mode: 'action' },
  duplicate_campaign: { label: 'Duplicated campaign', mode: 'action' },
  update_campaign_schedule: { label: 'Updated schedule', mode: 'action' },
  update_geo_targeting: { label: 'Updated geo targeting', mode: 'action' },
  update_frequency_cap: { label: 'Updated frequency cap', mode: 'action' },
  reallocate_budget: { label: 'Reallocated budget', mode: 'action' },
  get_ssp_creatives: { label: 'SSP creatives', mode: 'query' },
  delete_ssp_creative: { label: 'Deleted SSP creative', mode: 'action' },
  search_audience_segments: { label: 'Searched audiences', mode: 'query' },
  sync_audience_segments: { label: 'Synced audiences', mode: 'action' },
  update_campaign_audiences: { label: 'Updated audiences', mode: 'action' },
  update_supply_restrictions: { label: 'Updated supply', mode: 'action' },
  update_dayparts: { label: 'Updated dayparts', mode: 'action' },
  get_campaign_analytics_summary: { label: 'Campaign analytics', mode: 'query' },
  compare_campaigns: { label: 'Compared campaigns', mode: 'query' },
  get_workspace_overview: { label: 'Workspace overview', mode: 'query' },
  find_at_risk_customers: { label: 'At-risk customers', mode: 'query' },
  compare_verticals: { label: 'Compared verticals', mode: 'query' },
  get_revenue_pipeline: { label: 'Revenue pipeline', mode: 'query' },
  suggest_outreach: { label: 'Outreach suggestions', mode: 'query' },
  recommend_impression_plan: { label: 'Impression recommendation', mode: 'action' },
}

const TOOL_ICONS: Record<string, IconSvgElement> = {
  __thinking: BrainIcon,
  remember_user_preference: SparklesIcon,
  reinforce_memory: BrainIcon,
  create_campaign: Megaphone02Icon,
  update_campaign: PencilEdit01Icon,
  update_campaign_alerts: Settings01Icon,
  pause_campaign: PauseIcon,
  resume_campaign: PlayIcon,
  update_bid: Dollar01Icon,
  update_pacing: ChartBarLineIcon,
  get_campaign_performance: ChartIcon,
  get_atrium_performance_report: ChartAnalysisIcon,
  export_report_csv: Upload05Icon,
  upload_creative_to_ssp: Upload05Icon,
  create_video: Video01Icon,
  generate_video: Video01Icon,
  attach_creative: Link01Icon,
  scrape_brand: AiSearchIcon,
  update_brand: PaintBoardIcon,
  get_brand_context: PaintBoardIcon,
  get_campaigns: Megaphone01Icon,
  get_campaign_details: InformationCircleIcon,
  get_benchmarks: ChartAnalysisIcon,
  get_creatives: Image01Icon,
  recall_workspace_context: BrainIcon,
  get_platform_health: HealthIcon,
  analyze_workspace: AiBrain01Icon,
  read_all_memory: Database01Icon,
  query_platform_metrics: ChartIcon,
  search_conversations: Search01Icon,
  search_chat_history: Search01Icon,
  get_wallet_balance: Wallet01Icon,
  check_wallet_balance: Wallet01Icon,
  get_ssp_campaign_status: DashboardCircleIcon,
  get_ssp_performance: ChartBarLineIcon,
  get_video_status: Video01Icon,
  get_wallet_transactions: TransactionIcon,
  add_wallet_funds: AddMoneyCircleIcon,
  allocate_campaign_budget: DollarCircleIcon,
  launch_campaign: RocketIcon,
  update_budget: Dollar01Icon,
  sonobi_query: Database02Icon,
  navigate: MapsIcon,
  show_options: InformationCircleIcon,
  update_notification_preferences: Notification01Icon,
  get_connected_channels: Link01Icon,
  delete_campaign: Delete01Icon,
  duplicate_campaign: CopyIcon,
  update_campaign_schedule: Calendar04Icon,
  update_geo_targeting: MapsIcon,
  update_frequency_cap: FilterIcon,
  reallocate_budget: Dollar01Icon,
  get_ssp_creatives: Image01Icon,
  delete_ssp_creative: Delete01Icon,
  search_audience_segments: UserGroupIcon,
  sync_audience_segments: RefreshIcon,
  update_campaign_audiences: UserGroupIcon,
  ua_create_audience_from_attachment: UserGroupIcon,
  ua_get_audience_ingest_status: UserGroupIcon,
  update_supply_restrictions: GlobeIcon,
  update_dayparts: Calendar04Icon,
  get_campaign_analytics_summary: ChartAnalysisIcon,
  compare_campaigns: ChartBarLineIcon,
  get_workspace_overview: DashboardCircleIcon,
  find_at_risk_customers: AlertCircleIcon,
  compare_verticals: ChartIcon,
  get_revenue_pipeline: DollarCircleIcon,
  suggest_outreach: Mail01Icon,
  recommend_impression_plan: AddMoneyCircleIcon,
}

function getConfig(toolName: string): ToolConfig {
  return (
    TOOL_REGISTRY[toolName] ?? {
      label: toolName.replace(/_/g, ' '),
      mode: 'action' as const,
    }
  )
}

function getToolIcon(toolName: string): IconSvgElement {
  return TOOL_ICONS[toolName] ?? ToolsIcon
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ToolCallState {
  name: string
  input: Record<string, unknown>
  result?: unknown
  error?: string
  status: 'pending' | 'complete' | 'error'
}

// ─── Result formatting ────────────────────────────────────────────────────────

const MAX_DEPTH = 6

function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number' && !Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function ToolResultValue({ value, depth }: { value: unknown; depth: number }) {
  if (depth > MAX_DEPTH) {
    return (
      <span className="break-all font-mono text-[10px] text-[#939294]">
        {typeof value === 'object' ? JSON.stringify(value) : formatPrimitive(value)}
      </span>
    )
  }

  if (value === null || value === undefined) {
    return <span className="text-[#939294]">—</span>
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <span className="text-atrium-black">{formatPrimitive(value)}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-[#939294]">None</span>
    }

    const objectsOnly = value.every(
      (item) => item !== null && typeof item === 'object' && !Array.isArray(item)
    )

    if (objectsOnly) {
      return (
        <ul className="mt-1 space-y-2">
          {(value as Record<string, unknown>[]).map((item, i) => (
            <li
              key={i}
              className="rounded-md border border-atrium-border/40 bg-neutral-100/40 px-2.5 py-2 dark:bg-atrium-inner/80"
            >
              <ToolResultFields record={item} depth={depth + 1} />
            </li>
          ))}
        </ul>
      )
    }

    return (
      <ul className="mt-1 list-disc space-y-1 pl-4 marker:text-[#939294]">
        {value.map((item, i) => (
          <li key={i} className="text-atrium-black">
            <ToolResultValue value={item} depth={depth + 1} />
          </li>
        ))}
      </ul>
    )
  }

  if (typeof value === 'object') {
    return <ToolResultFields record={value as Record<string, unknown>} depth={depth + 1} />
  }

  return <span className="text-atrium-black">{formatPrimitive(value)}</span>
}

function ToolResultFields({
  record,
  depth,
}: {
  record: Record<string, unknown>
  depth: number
}) {
  const entries = Object.entries(record).filter(([k]) => !k.startsWith('__'))
  if (entries.length === 0) {
    return <span className="text-[#939294]">No fields</span>
  }

  return (
    <dl className="space-y-2 text-xs">
      {entries.map(([key, val]) => (
        <div
          key={key}
          className="grid gap-x-3 gap-y-0.5 sm:grid-cols-[minmax(5rem,10rem)_minmax(0,1fr)]"
        >
          <dt className="text-[#939294]">{formatKey(key)}</dt>
          <dd className="min-w-0 text-atrium-black">
            <ToolResultValue value={val} depth={depth} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function ToolResultSummary({ data }: { data: unknown }) {
  return (
    <div className="max-h-72 overflow-y-auto pr-1 text-xs leading-relaxed">
      <ToolResultValue value={data} depth={0} />
    </div>
  )
}

// ─── Shared accordion shell ───────────────────────────────────────────────────

/** Matches trigger gutter so the thread lines up under the icon / chevron. */
const TOOL_GUTTER = 'flex w-5 shrink-0 justify-center'

/** Single left slot: tool icon by default, ArrowDown01Icon on hover (accordion chevron). */
function ToolTriggerLeadingIcon({ toolIcon }: { toolIcon: IconSvgElement }) {
  return (
    <span className="relative size-4 shrink-0">
      <HugeiconsIcon
        icon={toolIcon}
        className="absolute inset-0 size-4 text-[#939294] transition-opacity duration-200 group-hover/toolcall:pointer-events-none group-hover/toolcall:opacity-0"
      />
      <HugeiconsIcon
        icon={ArrowDown01Icon}
        className="absolute inset-0 size-4 text-[#939294] opacity-0 transition-all duration-200 group-hover/toolcall:opacity-100 group-data-[state=open]/toolcall:rotate-180"
      />
    </span>
  )
}

function ToolAccordionShell({
  toolName,
  titleExtra,
  titleClassName,
  children,
}: {
  toolName: string
  titleExtra?: ReactNode
  titleClassName?: string
  children: ReactNode
}) {
  const accId = useId().replace(/:/g, '')
  const toolIcon = getToolIcon(toolName)

  return (
    <div className="my-2 w-full min-w-0 max-w-full">
      <Accordion type="single" collapsible className="w-full min-w-0">
        <AccordionItem value={accId || 'tool'} className="w-full min-w-0 border-0">
          <AccordionTrigger
            showChevron={false}
            className="group/toolcall w-full min-w-0 items-start gap-2 py-2 hover:no-underline [&[data-state=open]]:hover:no-underline"
          >
            <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
              <ToolTriggerLeadingIcon toolIcon={toolIcon} />
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left">
              <span className={cn('text-[13px] font-medium text-atrium-black', titleClassName)}>
                {getConfig(toolName).label}
              </span>
              {titleExtra}
            </div>
          </AccordionTrigger>
          <AccordionContent className="w-full min-w-0 pb-3 pt-0 text-atrium-black">
            <div className="flex w-full min-w-0 gap-2">
              <div className={cn(TOOL_GUTTER, 'relative self-stretch')}>
                <span
                  className="pointer-events-none absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-atrium-border/60"
                  aria-hidden
                />
              </div>
              <div className="min-w-0 flex-1 pl-0">{children}</div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ToolCallSkeleton({ toolName }: { toolName: string }) {
  const config = getConfig(toolName)

  return (
    <div className="my-2 flex w-full min-w-0 max-w-full items-center gap-2 py-2 text-[13px] text-[#939294]">
      <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
        <HugeiconsIcon icon={Loading02Icon} className="size-4 shrink-0 animate-spin text-[#939294]/70" />
      </div>
      <span className="min-w-0 animate-pulse">Running {config.label.toLowerCase()}…</span>
    </div>
  )
}

export function ThinkingToolCard({ content, isStreaming }: { content: string; isStreaming?: boolean }) {
  if (!content && !isStreaming) return null

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0
  const titleExtra = isStreaming ? (
    <span className="text-[13px] font-normal text-[#939294]">working</span>
  ) : wordCount > 0 ? (
    <span className="text-xs font-normal text-[#939294]">~{wordCount} words</span>
  ) : null

  return (
    <ToolAccordionShell toolName="__thinking" titleExtra={titleExtra}>
      {content ? (
        <div className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[#939294]">
          {content}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-[#939294]">
          <HugeiconsIcon icon={Loading02Icon} className="size-3.5 animate-spin" />
          <span>Reasoning through this...</span>
        </div>
      )}
    </ToolAccordionShell>
  )
}

function extractEntityName(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null
  const r = result as Record<string, unknown>
  const name = r.name || r.title || r.campaign_name
  return typeof name === 'string' ? name : null
}

function ActionToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const entityName = extractEntityName(toolCall.result)
  const result = toolCall.result as Record<string, unknown> | undefined
  const queueId = typeof result?.queued_id === 'string' ? result.queued_id : null
  const titleExtra = entityName ? (
    <span className="truncate text-[13px] font-normal text-[#939294]">&ldquo;{entityName}&rdquo;</span>
  ) : null

  return (
    <ToolAccordionShell toolName={toolCall.name} titleExtra={titleExtra}>
      <div className="space-y-3">
        <section>
          <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#939294]">
            Input
          </h4>
          <ToolResultSummary data={toolCall.input} />
        </section>
        {toolCall.result !== undefined && (
          <section>
            <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#939294]">
              Result
            </h4>
            <ToolResultSummary data={toolCall.result} />
            {queueId && <SupplyConfigWritePill queueId={queueId} />}
          </section>
        )}
      </div>
    </ToolAccordionShell>
  )
}

function QueryToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const result = toolCall.result
  const isArray = Array.isArray(result)
  const count = isArray ? (result as unknown[]).length : null

  const titleExtra =
    count !== null ? (
      <span className="text-xs font-normal text-[#939294]">
        ({count} {count === 1 ? 'item' : 'items'})
      </span>
    ) : null

  return (
    <ToolAccordionShell toolName={toolCall.name} titleExtra={titleExtra}>
      <ToolResultSummary data={result} />
    </ToolAccordionShell>
  )
}

function ErrorToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const titleExtra = (
    <span className="text-[13px] font-normal text-red-600">failed</span>
  )

  return (
    <ToolAccordionShell
      toolName={toolCall.name}
      titleExtra={titleExtra}
      titleClassName="text-red-600"
    >
      <div className="space-y-3">
        {toolCall.error && (
          <p className="text-xs font-medium text-red-600/90">{toolCall.error}</p>
        )}
        <section>
          <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#939294]">
            Input
          </h4>
          <ToolResultSummary data={toolCall.input} />
        </section>
      </div>
    </ToolAccordionShell>
  )
}

function PlanLimitCard({ toolCall }: { toolCall: ToolCallState }) {
  const result = toolCall.result as Record<string, unknown>
  const upgradeTo = result.upgrade_to as string
  const config = getConfig(toolCall.name)
  const toolIcon = getToolIcon(toolCall.name)

  return (
    <div className="my-2 flex w-full min-w-0 max-w-full flex-col gap-2 py-2">
      <div className="flex w-full min-w-0 items-center gap-2 text-[13px]">
        <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
          <HugeiconsIcon icon={toolIcon} className="size-4 shrink-0 text-amber-700/90" />
        </div>
        <div className="min-w-0 flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium text-amber-800">{config.label}</span>
          <span className="text-amber-700">· Plan limit reached</span>
        </div>
      </div>
      <p className="w-full pl-7 text-xs text-amber-700/90">{result.message as string}</p>
      {upgradeTo && (
        <a
          href="/usage/plans"
          className="w-full pl-7 text-xs font-medium text-accent-600 hover:text-accent-700 transition-colors"
        >
          Upgrade to {upgradeTo.charAt(0).toUpperCase() + upgradeTo.slice(1)} →
        </a>
      )}
    </div>
  )
}

function ImpressionRecommendationCard({ toolCall }: { toolCall: ToolCallState }) {
  const router = useRouter()
  const result = toolCall.result as Record<string, unknown> | undefined
  if (!result || result.error) return <ActionToolCard toolCall={toolCall} />

  const uiAction = result._ui_action as Record<string, unknown> | undefined
  if (!uiAction) return <ActionToolCard toolCall={toolCall} />

  const label = uiAction.label as string
  const impressions = uiAction.impressions as number
  const priceDisplay = uiAction.price_display as string
  const reason = result.reason as string
  const tierType = uiAction.tier_type as string

  function fmtImp(n: number) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
    return n.toLocaleString()
  }

  const toolIcon = getToolIcon(toolCall.name)

  return (
    <div className="my-2 w-full min-w-0 max-w-full">
      <div className="flex w-full min-w-0 items-start gap-2 py-2">
        <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
          <HugeiconsIcon icon={toolIcon} className="size-4 shrink-0 text-[#939294]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-atrium-border bg-atrium-sidebar p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-atrium-black/50 mb-0.5">
                  {label} Impressions
                </p>
                <p className="text-2xl font-bold text-atrium-black tabular-nums">
                  {fmtImp(impressions)}
                </p>
                <p className="text-sm text-atrium-black/50 mt-0.5">
                  {priceDisplay} {tierType === 'premium' ? '· $35 CPM' : '· $20 CPM'}
                </p>
              </div>
              <span className={cn(
                'shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full',
                tierType === 'premium'
                  ? 'bg-accent-600/10 text-accent-700'
                  : 'bg-atrium-content text-atrium-black/70'
              )}>
                {label}
              </span>
            </div>
            {reason && (
              <p className="text-sm text-atrium-black/60 leading-relaxed">{reason}</p>
            )}
            <Button
              size="sm"
              className="w-full"
              onClick={() => router.push('/usage')}
            >
              Purchase {fmtImp(impressions)} impressions
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function InstallPromptToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const result = toolCall.result as Record<string, unknown> | undefined
  const input = toolCall.input as Record<string, unknown> | undefined
  if (!result?.prompt || !input?.brand_id) return <ActionToolCard toolCall={toolCall} />

  return (
    <div className="my-2 w-full min-w-0 max-w-full">
      <div className="flex w-full min-w-0 items-start gap-2 py-2">
        <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
          <HugeiconsIcon icon={ToolsIcon} className="size-4 shrink-0 text-[#939294]" />
        </div>
        <div className="min-w-0 flex-1 rounded-xl border border-atrium-border bg-atrium-sidebar p-4">
          <InstallPromptModal
            compact
            brandId={input.brand_id as string}
            prompt={result.prompt as string}
            frameworkDetected={result.framework_detected as string}
            copyLink={result.copy_link as string}
          />
        </div>
      </div>
    </div>
  )
}

function AudienceIngestToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const result = toolCall.result as Record<string, unknown> | undefined
  const jobId = typeof result?.job_id === 'string' ? result.job_id : null

  return (
    <div>
      <ActionToolCard toolCall={toolCall} />
      {jobId && (
        <div className="pl-7">
          <AudienceIngestProgress jobId={jobId} />
        </div>
      )}
    </div>
  )
}

function CsvDownloadToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const result = toolCall.result as Record<string, unknown> | undefined
  if (!result?.csv_content) return <QueryToolCard toolCall={toolCall} />

  const csvContent = result.csv_content as string
  const filename = (result.filename as string) || 'atrium-report.csv'
  const rowCount = result.row_count as number | undefined
  const toolIcon = getToolIcon(toolCall.name)

  function handleDownload() {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="my-2 w-full min-w-0 max-w-full">
      <div className="flex w-full min-w-0 items-start gap-2 py-2">
        <div className={cn(TOOL_GUTTER, 'self-start pt-0.5')}>
          <HugeiconsIcon icon={toolIcon} className="size-4 shrink-0 text-[#939294]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between rounded-xl border border-atrium-border bg-atrium-sidebar px-4 py-3 gap-3">
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-atrium-black truncate">{filename}</p>
              {rowCount !== undefined && (
                <p className="text-xs text-[#939294] mt-0.5">{rowCount} rows</p>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={handleDownload} className="shrink-0">
              Download CSV
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function ToolCallCard({ toolCall }: { toolCall: ToolCallState }) {
  if (toolCall.status === 'pending') {
    return <ToolCallSkeleton toolName={toolCall.name} />
  }
  if (toolCall.status === 'error') {
    return <ErrorToolCard toolCall={toolCall} />
  }

  const result = toolCall.result as Record<string, unknown> | undefined
  if (result?.error === 'plan_limit') {
    return <PlanLimitCard toolCall={toolCall} />
  }

  if (toolCall.name === 'recommend_impression_plan') {
    return <ImpressionRecommendationCard toolCall={toolCall} />
  }

  if (toolCall.name === 'generate_install_prompt') {
    return <InstallPromptToolCard toolCall={toolCall} />
  }

  if (toolCall.name === 'ua_create_audience_from_attachment') {
    return <AudienceIngestToolCard toolCall={toolCall} />
  }

  if (toolCall.name === 'export_report_csv') {
    return <CsvDownloadToolCard toolCall={toolCall} />
  }

  const config = getConfig(toolCall.name)
  if (config.mode === 'action') {
    return <ActionToolCard toolCall={toolCall} />
  }
  return <QueryToolCard toolCall={toolCall} />
}
